import { ElementType, MouseEvent, useState } from 'react';

import { Typography, TypographyProps } from '@mui/material';

/**
 * What: a one-line `Typography` (`noWrap`, ends with an ellipsis) that shows the full text as a
 * native tooltip, but only when the text is actually cut off.
 * Why: table cells truncate long values; the operator must be able to read the whole value
 * without every cell carrying a tooltip.
 * Constraints:
 *  - The cut-off check (`scrollWidth > clientWidth`) runs on mouse enter, so it uses the width the
 *    cell has at that moment (column resize, zoom).
 *  - Only string or number children get a `title`; other nodes (elements) are rendered as-is.
 *  - `component` and the other Typography props pass through.
 */
export const TruncatedTypography = ({
  children,
  onMouseEnter,
  ...props
}: TypographyProps & { component?: ElementType }) => {
  const [title, setTitle] = useState<string>();
  const isText = typeof children === 'string' || typeof children === 'number';

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    if (isText) {
      const { scrollWidth, clientWidth } = event.currentTarget;
      setTitle(scrollWidth > clientWidth ? String(children) : undefined);
    }

    onMouseEnter?.(event);
  };

  return (
    <Typography noWrap {...props} title={title} onMouseEnter={handleMouseEnter}>
      {children}
    </Typography>
  );
};
